import { Stack, Typography } from "@mui/material";

import { ExpandLess, ExpandMore } from "@mui/icons-material";
import dayjs from "dayjs";
import React from "react";
import {
  CustomSchemaFieldResult,
  ExtractionResult,
  ParsingType,
} from "../../interfaces/doc";

const formatAddress = (address: any): string => {
  const parts: string[] = [];

  const streetParts = [address.number, address.way].filter(Boolean).join(" ");

  if (streetParts) parts.push(streetParts);

  const localityParts = [address.postal_code, address.city]
    .filter(Boolean)
    .join(" ");

  if (localityParts) parts.push(localityParts);

  if (address.country) parts.push(address.country);

  return parts.join(", ");
};

const formatName = (name: any): string => {
  return [name.civility, name.firstname, name.lastname]
    .filter(Boolean)
    .join(" ");
};

const formatMeasure = (measure: any): string => {
  return [measure.value, measure.unit].filter(Boolean).join(" ");
};

const formatCurrency = (currency: any): string => {
  return [currency.value, currency.currency].filter(Boolean).join(" ");
};

const ExtractionValue: React.FC<{
  value: any;
  parsing_type: ParsingType;
  name?: string;
  custom_schema?: CustomSchemaFieldResult[];
}> = ({ name, value, parsing_type, custom_schema }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  if (value === null) {
    return (
      <div className="flex flex-col gap-0.5 items-start">
        {name && (
          <Typography className="font-semibold text-sm text-[#4c4fa2]">
            {name}
          </Typography>
        )}
        <Typography className="text-gray-400">
          Pas de réponse trouvée
        </Typography>
      </div>
    );
  } else if (parsing_type === ParsingType.Date) {
    return (
      <div className="flex flex-col gap-0.5 items-start">
        {name && (
          <Typography className="font-semibold text-sm text-[#4c4fa2]">
            {name}
          </Typography>
        )}
        <Typography>{dayjs(value).format("DD/MM/YYYY")}</Typography>
      </div>
    );
  } else if (parsing_type === ParsingType.Boolean) {
    return (
      <div className="flex flex-col gap-0.5 items-start">
        {name && (
          <Typography className="font-semibold text-sm text-[#4c4fa2]">
            {name}
          </Typography>
        )}
        <Typography>{value ? "Oui" : "Non"}</Typography>
      </div>
    );
  } else if (
    [
      ParsingType.String,
      ParsingType.Int,
      ParsingType.Float,
      ParsingType.Enum,
    ].includes(parsing_type)
  ) {
    return (
      <div className="flex flex-col gap-0.5 items-start">
        {name && (
          <Typography className="font-semibold text-sm text-[#4c4fa2]">
            {name}
          </Typography>
        )}
        <Typography>{value}</Typography>
      </div>
    );
  } else if (parsing_type === ParsingType.Address) {
    return (
      <div className="flex flex-col gap-0.5 items-start">
        {name && (
          <Typography className="font-semibold text-sm text-[#4c4fa2]">
            {name}
          </Typography>
        )}
        <Typography>{formatAddress(value)}</Typography>
      </div>
    );
  } else if (parsing_type === ParsingType.Measure) {
    return (
      <div className="flex flex-col gap-0.5 items-start">
        {name && (
          <Typography className="font-semibold text-sm text-[#4c4fa2]">
            {name}
          </Typography>
        )}
        <Typography>{formatMeasure(value)}</Typography>
      </div>
    );
  } else if (parsing_type === ParsingType.Currency) {
    return (
      <div className="flex flex-col gap-0.5 items-start">
        {name && (
          <Typography className="font-semibold text-sm text-[#4c4fa2]">
            {name}
          </Typography>
        )}
        <Typography>{formatCurrency(value)}</Typography>
      </div>
    );
  } else if (parsing_type === ParsingType.Name) {
    return (
      <div className="flex flex-col gap-0.5 items-start">
        {name && (
          <Typography className="font-semibold text-sm text-[#4c4fa2]">
            {name}
          </Typography>
        )}
        <Typography>{formatName(value)}</Typography>
      </div>
    );
  } else if (parsing_type === ParsingType.Custom) {
    return (
      custom_schema && (
        <div className="flex items-start gap-1">
          {name && (
            <Typography className="font-semibold text-sm text-[#4c4fa2]">
              {name}
            </Typography>
          )}
          {isExpanded ? (
            <ExpandLess
              fontSize="small"
              onClick={() => setIsExpanded(!isExpanded)}
            />
          ) : (
            <ExpandMore
              fontSize="small"
              onClick={() => setIsExpanded(!isExpanded)}
            />
          )}
          {isExpanded ? (
            <Stack className="border-solid border-0 border-l-2 border-[#4c4fa2] pl-2 gap-3">
              {Object.keys(value).map((subKey) => {
                const parsing_type = custom_schema.filter(
                  ({ name }) => name === subKey
                )[0]?.parsing_type as ParsingType;
                return (
                  <ExtractionValue
                    name={subKey}
                    value={value[subKey]}
                    parsing_type={parsing_type}
                    key={subKey}
                  />
                );
              })}
            </Stack>
          ) : (
            <Typography>{`${Object.values(value).length} éléments`}</Typography>
          )}
        </div>
      )
    );
  }
};

const ExtractionDisplay: React.FC<{
  result: ExtractionResult;
}> = ({ result }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  if (Array.isArray(result.value)) {
    if (result.value.length === 0) {
      return (
        <ExtractionValue
          name={result.name}
          value={null}
          parsing_type={result.parsing_type}
          custom_schema={
            result.custom_schema ? result.custom_schema : undefined
          }
        />
      );
    } else {
      return (
        <div className="flex flex-col gap-1 items-start">
          <div className="flex gap-2">
            <Typography className="font-semibold text-sm text-[#4c4fa2]">
              {result.name}
            </Typography>
            {isExpanded ? (
              <ExpandLess
                fontSize="small"
                onClick={() => setIsExpanded(!isExpanded)}
              />
            ) : (
              <ExpandMore
                fontSize="small"
                onClick={() => setIsExpanded(!isExpanded)}
              />
            )}
          </div>
          {isExpanded ? (
            <Stack className="pl-2 gap-3">
              {result.value.map((v, index) => (
                <ExtractionValue
                  key={`${result.name}_${index}`}
                  value={v}
                  parsing_type={result.parsing_type}
                  custom_schema={
                    result.custom_schema ? result.custom_schema : undefined
                  }
                />
              ))}
            </Stack>
          ) : (
            <Typography>{`${result.value.length} éléments`}</Typography>
          )}
        </div>
      );
    }
  } else {
    return (
      <ExtractionValue
        key={result.name}
        name={result.name}
        value={result.value}
        parsing_type={result.parsing_type}
        custom_schema={result.custom_schema ? result.custom_schema : undefined}
      />
    );
  }
};

export default ExtractionDisplay;
